import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, CheckCircle, Clock, XCircle, AlertCircle, ChevronRight, Plus, Pencil, Trash2, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/apiClient';

interface Agency {
  id: number;
  name: string;
  abn: string;
  contactEmail: string;
  contactPhone?: string;
  contactName?: string;
  address?: string;
  status: string;
  currentLeaseCount: number;
  subscriptionPlanId?: number;
  createdAt: string;
}

interface AgencyFormData {
  name: string;
  abn: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  address: string;
}

const emptyForm: AgencyFormData = { name: '', abn: '', contactEmail: '', contactName: '', contactPhone: '', address: '' };

function getStatusBadge(status: string) {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</span>;
    case 'PENDING':
    case 'PENDING_EMAIL_VERIFICATION':
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>;
    case 'SUSPENDED':
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" />Suspended</span>;
    default:
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
  }
}

function AgencyModal({ agency, onClose, onSave }: {
  agency?: Agency | null;
  onClose: () => void;
  onSave: (data: AgencyFormData) => void;
}) {
  const [form, setForm] = useState<AgencyFormData>(agency ? {
    name: agency.name ?? '',
    abn: agency.abn ?? '',
    contactEmail: agency.contactEmail ?? '',
    contactName: agency.contactName ?? '',
    contactPhone: agency.contactPhone ?? '',
    address: agency.address ?? '',
  } : emptyForm);
  const [saving, setSaving] = useState(false);

  const set = (k: keyof AgencyFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{agency ? 'Edit Agency' : 'New Agency'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name *</label>
            <input required value={form.name} onChange={set('name')} placeholder="e.g. Sunrise Realty" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ABN</label>
            <input value={form.abn} onChange={set('abn')} placeholder="12 345 678 901" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input value={form.contactName} onChange={set('contactName')} placeholder="Full name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input value={form.contactPhone} onChange={set('contactPhone')} placeholder="0400 000 000" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input type="email" value={form.contactEmail} onChange={set('contactEmail')} placeholder="admin@agency.com" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input value={form.address} onChange={set('address')} placeholder="123 Main St, Sydney NSW 2000" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {saving ? 'Saving...' : agency ? 'Save Changes' : 'Create Agency'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminAgenciesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAgency, setEditAgency] = useState<Agency | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agency | null>(null);
  const [actionError, setActionError] = useState('');

  const { data: agencies = [], isLoading, isError, error } = useQuery<Agency[]>({
    queryKey: ['admin', 'agencies', statusFilter, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      const qs = params.toString();
      return api.get<Agency[]>(`/admin/agencies${qs ? `?${qs}` : ''}`).then(r => r ?? []);
    },
    staleTime: 30_000,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.put(`/admin/agencies/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });

  const createAgency = useMutation({
    mutationFn: (data: AgencyFormData) => api.post('/admin/agencies', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] }); setShowModal(false); },
    onError: (e: any) => setActionError(e.message ?? 'Failed to create agency'),
  });

  const updateAgency = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgencyFormData }) =>
      api.put(`/admin/agencies/${id}/details`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] }); setEditAgency(null); },
    onError: (e: any) => setActionError(e.message ?? 'Failed to update agency'),
  });

  const deleteAgency = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/agencies/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] }); setDeleteTarget(null); },
    onError: (e: any) => setActionError(e.message ?? 'Failed to delete agency'),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between mb-6">
        <PageHeader
          title="Manage Agencies"
          subtitle="View and manage all registered agencies"
          icon={<Building2 className="w-8 h-8" />}
        />
        <button
          onClick={() => { setActionError(''); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> New Agency
        </button>
      </div>

      {(isError || actionError) && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{actionError || (error as Error)?.message || 'Failed to load agencies'}</div>
          {actionError && <button onClick={() => setActionError('')} className="ml-auto text-red-600"><X className="w-4 h-4" /></button>}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, ABN, or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <p className="text-sm text-gray-500">{agencies.length} agenc{agencies.length !== 1 ? 'ies' : 'y'}</p>
          </div>
          {agencies.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No agencies found.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {agencies.map(agency => (
                <div key={agency.id} className="p-4 sm:p-6 flex items-center justify-between gap-4 hover:bg-gray-50">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/admin/agencies/${agency.id}`)}
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-medium text-gray-900 truncate">{agency.name}</p>
                      {getStatusBadge(agency.status)}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{agency.contactEmail}</p>
                    <p className="text-xs text-gray-400 mt-1">ABN: {agency.abn || '—'} · {agency.currentLeaseCount ?? 0} active leases</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={agency.status}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateStatus.mutate({ id: agency.id, status: e.target.value })}
                      className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary/30"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="PENDING">Pending</option>
                      <option value="SUSPENDED">Suspended</option>
                    </select>
                    <button
                      onClick={e => { e.stopPropagation(); setActionError(''); setEditAgency(agency); }}
                      className="p-1.5 text-gray-500 hover:text-primary hover:bg-primary/10 rounded"
                      title="Edit agency"
                    ><Pencil className="w-4 h-4" /></button>
                    <button
                      onClick={e => { e.stopPropagation(); setActionError(''); setDeleteTarget(agency); }}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete agency"
                    ><Trash2 className="w-4 h-4" /></button>
                    <ChevronRight
                      className="w-5 h-5 text-gray-400 cursor-pointer"
                      onClick={() => navigate(`/admin/agencies/${agency.id}`)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <AgencyModal
          onClose={() => setShowModal(false)}
          onSave={data => createAgency.mutateAsync(data)}
        />
      )}

      {/* Edit Modal */}
      {editAgency && (
        <AgencyModal
          agency={editAgency}
          onClose={() => setEditAgency(null)}
          onSave={data => updateAgency.mutateAsync({ id: editAgency.id, data })}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Agency</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => deleteAgency.mutate(deleteTarget.id)}
                disabled={deleteAgency.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteAgency.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
